/**
 * PBSCO Consent Manager v2.1
 * Hooks into the existing HubSpot cookie banner to fire real consent signals.
 *
 * The HubSpot banner (id="hs-banner-parent") already provides the UI.
 * This script makes the Accept / Decline buttons actually:
 *   1. Update Google Consent Mode v2 signals
 *   2. Update Shopify Customer Privacy API
 *   3. Load Hotjar on accept (deferred from theme.liquid)
 *   4. Clear tracker cookies on decline
 *   5. Persist the choice so signals are re-applied on every subsequent page
 *
 * Session replay: Hotjar only (Clarity removed).
 *
 * HubSpot button IDs:
 *   Accept  → #hs-eu-confirmation-button
 *   Decline → #hs-eu-decline-button
 *
 * HubSpot prior-choice cookie: __hs_cookie_cat_pref
 *   Format: "1:true_2:true_3:true"  (all accepted)
 *           "1:false_2:false_3:false" (all declined)
 *   Categories: 1=analytics, 2=advertisement, 3=functionality
 *
 * Our own storage key: localStorage 'pbsco_consent' = 'accepted' | 'declined'
 * (Used as the source of truth on subsequent page loads before HubSpot renders)
 *
 * Tracker cookies cleared on decline (from audit, 7 Jun 2026):
 *   _clck, _clsk (Clarity — cleared even though removed, to clean up any remnants),
 *   _fbp (Meta), _ga, _ga_* (GA4), _gcl_au (Google Ads),
 *   _uetsid, _uetvid (Bing/UET), _ttp, ttcsid* (TikTok), _rdt_uuid (Reddit),
 *   _pin_unauth (Pinterest), _hjSession*, _hjSessionUser* (Hotjar),
 *   _privy_*, __ps_* (Privy/ParticleSpace — cleared to remove any remnants),
 *   MUID, NID, ANONCHK, CLID, MR (Microsoft cross-domain)
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'pbsco_consent';
  var HS_COOKIE_NAME = '__hs_cookie_cat_pref';
  var COOKIE_DOMAIN = '.' + window.location.hostname.replace(/^www\./, '');
  var _hookAttempts = 0;
  var _maxHookAttempts = 40; // 40 × 250ms = 10 seconds max wait

  // ─── Tracker cookie prefixes to clear on decline ───────────────────────────
  var TRACKER_COOKIE_PREFIXES = [
    '_clck', '_clsk',
    '_fbp',
    '_ga',
    '_gcl_au', '_gcl_aw', '_gcl_dc', '_gcl_gb',
    '_uetsid', '_uetvid',
    '_ttp', 'ttcsid',
    '_rdt_uuid',
    '_pin_unauth',
    '_hjSession', '_hjSessionUser', '_hjid',
    '_privy_',
    '__ps_did', '__ps_fva', '__ps_lu', '__ps_sr',
    'MUID', 'NID', 'ANONCHK', 'CLID', 'MR'
  ];

  // ─── gtag helper ───────────────────────────────────────────────────────────
  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  // ─── Google Consent Mode v2 update ─────────────────────────────────────────
  function updateGoogleConsent(granted) {
    var state = granted ? 'granted' : 'denied';
    gtag('consent', 'update', {
      'ad_storage':              state,
      'analytics_storage':       state,
      'ad_user_data':            state,
      'ad_personalization':      state,
      'functionality_storage':   state,
      'personalization_storage': state,
      'security_storage':        'granted'
    });
  }

  // ─── Shopify Customer Privacy API update ────────────────────────────────────
  function updateShopifyConsent(granted) {
    var tryUpdate = function () {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        window.Shopify.customerPrivacy.setTrackingConsent(
          {
            marketing:    granted,
            analytics:    granted,
            preferences:  granted,
            sale_of_data: granted
          },
          function () {}
        );
        return true;
      }
      return false;
    };

    if (!tryUpdate()) {
      window.addEventListener('load', tryUpdate);
    }
  }

  // ─── Clear tracker cookies ──────────────────────────────────────────────────
  function clearTrackerCookies() {
    var cookies = document.cookie.split(';');
    var domains = [COOKIE_DOMAIN, window.location.hostname, ''];

    cookies.forEach(function (cookie) {
      var name = cookie.split('=')[0].trim();
      var shouldClear = TRACKER_COOKIE_PREFIXES.some(function (prefix) {
        return name === prefix || name.indexOf(prefix) === 0;
      });

      if (shouldClear) {
        domains.forEach(function (domain) {
          var domainStr = domain ? '; domain=' + domain : '';
          document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + domainStr;
          document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax' + domainStr;
        });
      }
    });
  }

  // ─── Load deferred theme-hardcoded trackers ─────────────────────────────────
  function loadDeferredTrackers() {
    if (typeof window.__pbsco_load_hotjar === 'function') {
      window.__pbsco_load_hotjar();
    }
    if (typeof window.__pbsco_load_impact === 'function') {
      window.__pbsco_load_impact();
    }
  }

  // ─── Persist our own consent key ──────────────────────────────────────────
  function persistChoice(value) {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      document.cookie = STORAGE_KEY + '=' + value + '; max-age=31536000; path=/; SameSite=Lax';
    }
  }

  // ─── Read our stored consent key ──────────────────────────────────────────
  function readOurStoredChoice() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      var match = document.cookie.match(new RegExp('(?:^|; )' + STORAGE_KEY + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    }
  }

  // ─── Read HubSpot's prior-choice cookie ────────────────────────────────────
  // Returns 'accepted', 'declined', or null.
  // HubSpot cookie format: "1:true_2:true_3:true" (all accept)
  //                        "1:false_2:false_3:false" (all decline)
  // We look at category 2 (advertisement) as the primary signal — if any
  // marketing category is true, we treat it as accepted.
  function readHubSpotChoice() {
    var match = document.cookie.match(new RegExp('(?:^|; )' + HS_COOKIE_NAME + '=([^;]*)'));
    if (!match) return null;

    try {
      var val = decodeURIComponent(match[1]);
      // Any "true" means the user previously accepted at least one category
      if (val.indexOf(':true') !== -1) return 'accepted';
      // All false = declined
      if (val.indexOf(':false') !== -1) return 'declined';
    } catch (e) {}

    return null;
  }

  // ─── ACCEPT handler ────────────────────────────────────────────────────────
  function onAccept() {
    persistChoice('accepted');
    updateGoogleConsent(true);
    updateShopifyConsent(true);
    loadDeferredTrackers();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'pbsco_consent_accepted' });
  }

  // ─── DECLINE handler ───────────────────────────────────────────────────────
  function onDecline() {
    persistChoice('declined');
    updateGoogleConsent(false);
    updateShopifyConsent(false);
    clearTrackerCookies();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'pbsco_consent_declined' });
  }

  // ─── Hook into HubSpot banner buttons ─────────────────────────────────────
  // HubSpot loads its banner asynchronously, so we use MutationObserver +
  // a periodic fallback to catch the buttons as soon as they appear in the DOM.
  function hookHubSpotButtons() {
    var acceptBtn = document.getElementById('hs-eu-confirmation-button');
    var declineBtn = document.getElementById('hs-eu-decline-button');

    if (acceptBtn && declineBtn) {
      // Avoid double-binding
      if (acceptBtn.dataset.pbscoHooked) return;
      acceptBtn.dataset.pbscoHooked = '1';
      declineBtn.dataset.pbscoHooked = '1';

      acceptBtn.addEventListener('click', onAccept);
      declineBtn.addEventListener('click', onDecline);

      // Also hook the "Cookies settings" modal save button if it exists
      var settingsBtn = document.getElementById('hs-eu-cookie-settings-button');
      if (settingsBtn) {
        // The modal accept/save button that may appear after "Cookie settings"
        // HubSpot injects it dynamically; watch for it too
        hookHubSpotModalButtons();
      }

      return true;
    }
    return false;
  }

  // Hook the HubSpot preferences modal save button (injected later by HubSpot)
  function hookHubSpotModalButtons() {
    var observer = new MutationObserver(function () {
      // HubSpot modal accept: id="hs-eu-confirmation-button" (reused) or
      // the save/confirm button inside the modal
      var modalAccept = document.getElementById('hs-eu-cookie-save-button') ||
                        document.querySelector('#hs-modal .hs-modal-submit');
      if (modalAccept && !modalAccept.dataset.pbscoHooked) {
        modalAccept.dataset.pbscoHooked = '1';
        modalAccept.addEventListener('click', onAccept);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Retry until HubSpot renders its banner (up to _maxHookAttempts × 250ms)
  function waitForHubSpotAndHook() {
    if (hookHubSpotButtons()) return; // already found

    _hookAttempts++;
    if (_hookAttempts >= _maxHookAttempts) return; // give up after 10s

    setTimeout(waitForHubSpotAndHook, 250);
  }

  // ─── PUBLIC API ────────────────────────────────────────────────────────────
  window.PBSCOConsent = {

    /**
     * Called on DOMContentLoaded.
     * - If we have a stored choice, silently re-apply consent signals.
     * - If not, check HubSpot's cookie (returning visitor who chose on HubSpot
     *   before this script was deployed).
     * - Always hook into the HubSpot buttons so future clicks are captured.
     */
    init: function () {
      var ourChoice = readOurStoredChoice();

      if (ourChoice === 'accepted') {
        updateGoogleConsent(true);
        updateShopifyConsent(true);
        loadDeferredTrackers();
      } else if (ourChoice === 'declined') {
        updateGoogleConsent(false);
        updateShopifyConsent(false);
        // Don't clear cookies again on page load — just keep signals denied
      } else {
        // No choice stored by us. Check if HubSpot already has a prior choice
        // (covers visitors who consented/declined before this script deployed).
        var hsChoice = readHubSpotChoice();
        if (hsChoice === 'accepted') {
          persistChoice('accepted');
          updateGoogleConsent(true);
          updateShopifyConsent(true);
          loadDeferredTrackers();
        } else if (hsChoice === 'declined') {
          persistChoice('declined');
          updateGoogleConsent(false);
          updateShopifyConsent(false);
        }
        // If neither cookie exists → first visit, banner will show (HubSpot handles this)
      }

      // Always hook the HubSpot buttons regardless of stored state,
      // so that if the user re-opens the banner they can change their choice.
      waitForHubSpotAndHook();
    },

    /** Programmatic accept (callable from console or custom code) */
    accept: onAccept,

    /** Programmatic decline (callable from console or custom code) */
    decline: onDecline,

    /** Clear all known tracker cookies */
    clearTrackerCookies: clearTrackerCookies,

    /** Reset stored choice so banner shows again on next page load */
    reset: function () {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        document.cookie = STORAGE_KEY + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      }
      // Also expire the HubSpot preference cookie so HubSpot shows its banner again
      document.cookie = HS_COOKIE_NAME + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }
  };

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.PBSCOConsent.init();
    });
  } else {
    window.PBSCOConsent.init();
  }

})();
